import _ from 'lodash';
import { createSelector } from 'reselect';

import {
  getActiveContainers,
  getComponentProps,
  getEntitiesDownloadingStates,
  getEntityData,
  getEntityDefinition,
  getEntityType,
  getEntityValues,
  getFormData,
  getFormInfo,
  getFormModel,
  getIsFormChangesMode,
  getIsLogonUserChanged,
  getOfflineDataEntities,
  getOriginalData,
  getPanelLayout,
  getPrimaryKeysMap,
  getSavedSearchResults,
  getSelectedSubGrids,
  getToken,
  getTranslations,
  getUserMadeInteraction,
  getWarnings,
  getWorkflowInfo,
} from './common.selectors';
import {
  getClosestParentWithEntityName,
  getEvaluatedConditions,
  getModelById,
  getSsContext,
} from './form-manager.selectors';
import * as Urls from '../../constants/Urls';
import { WARNING_SOURCES } from '../../constants/WarningsConstants';
import { pageHasErrors } from '../../selectors/MessagesSelectors';
import { isTrue } from '../../utils/commonUtils';
import {
  BUTTON_LIST,
  DATALOOKUP_TYPE_EXTERNAL_API,
  FORM_KEYS,
  FORM_MODEL_ATTRIBUTES,
  SS_CONTROL_TYPE,
} from '../constants/form-manager.constants';
import {
  BUTTON_TYPES_SUPPORT_DISABLE,
  DECISION_TREE_QUERY_PARAM_NAMES,
  DOWNLOAD_ICON_ACTIONS_TYPES,
  SELECTED_SUB_GRID_DEFAULT_WIDTH,
} from '../constants/runtime.constants';
import { getSmartSolveDiff } from '../utils/diffTracking.utils';
import { evalExpression, evalSSEexpression, evaluateConditions } from '../utils/evaluation.utils';
import {
  getDataPath,
  getFullDataSetByDataMember,
  getPropType,
  translate,
  updateRulesScripts,
} from '../utils/form-manager.utils';
import {
  getBladeGroupWidthArrEntityDisplayPage,
  getBladeGroupWidthArrWorkflowStepPage,
  getContainerWidthPercentEntityDisplayPage,
  getContainerWidthPercentWorkflowStepPage,
  getDataForController,
  getDynamicParamCollection,
  getIndexesForDataMember,
  getVisibleZoomByTextBoxId,
  isValidClusterColumnChartData,
  parseListString,
} from '../utils/runtime.utils';

export const getFileViewerParams = createSelector(
  [getEntityData, getEntityType, getComponentProps],
  (entityData, entityType, { documentRecordId, documentNumber, documentRevision }) => {
    return {
      parId: entityData[entityType]?.[documentRecordId],
      documentNumber: entityData[entityType]?.[documentNumber],
      documentRevision: entityData[entityType]?.[documentRevision],
    };
  }
);

export const getDataByController = createSelector(
  [getEntityData, getEntityDefinition, getComponentProps],
  (entityData, entityDefinition, { entityName, fieldName, index }) =>
    getDataForController({ entityDefinition, entityData, entityName, fieldName, index })
);

export const getDataSourceWatchValues = createSelector(
  [(state, props) => getSsContext(state, props), getComponentProps],
  (ssContext, { conditions, dataSource }) => {
    const { entityDefinition, data, gridArrIndex } = ssContext;
    const entityData = data.entityDataStore?.entityData ?? {};

    const watchValueFields = [];
    const formulaWatchValues = [];
    const formDataWatchValues = [];

    conditions.forEach((condition) => {
      if (condition.LeftType === 'column') {
        watchValueFields.push(condition.LeftValue);
      }
      if (condition.RightType === 'column') {
        watchValueFields.push(condition.RightValue);
      }
      if (condition.LeftType === 'formula') {
        formulaWatchValues.push(evalSSEexpression(condition.LeftValue, ssContext));
      }
      if (condition.RightType === 'formula') {
        formulaWatchValues.push(evalSSEexpression(condition.RightValue, ssContext));
      }
    });

    const watchValues = watchValueFields
      .map((field) => {
        const [entityName, fieldName] = field.split('.');
        const dataPath = getDataPath(entityName, gridArrIndex, entityDefinition);
        return _.get(entityData, [...dataPath, fieldName]);
      })
      .concat(formulaWatchValues);

    if (dataSource?.dataSourceTypeStr === 'FORMDATA') {
      const rows = getFullDataSetByDataMember(data, dataSource.dataSourceName);

      if (!Array.isArray(rows) || !dataSource.runtimeColumnList) {
        return watchValues;
      }

      rows.forEach((row) => {
        const selectedValues = dataSource.runtimeColumnList.map((column) => row[column]);
        formDataWatchValues.push(...selectedValues);
      });

      watchValues.push(...formDataWatchValues);
    }

    return watchValues;
  }
);

export const getGrandchidlrenForVirtualControl = createSelector(
  [getFormModel, getComponentProps],
  (formModel, { childrenIds }) =>
    Object.values(formModel).reduce((acc, model) => {
      if (childrenIds.includes(model.controlId)) {
        acc.push(...model.childrenNodes.map((id) => ({ id, parent: model.controlId })));
      }
      return acc;
    }, [])
);

export const isMasterSetupEntity = createSelector([getFormInfo], (formInfo) => {
  return formInfo.isMasterSetupEntity;
});

export const getClientChanges = (state, props) => {
  const originalData = getOriginalData(state, props);
  const data = getFormData(state, props);
  const entityType = getEntityType(state, props);
  const entityDefinition = getEntityDefinition(state, props);

  return getSmartSolveDiff(
    originalData.entityDataStore,
    data.entityDataStore,
    entityType,
    entityDefinition
  );
};

export const formHasUnsavedChanges = createSelector(
  [getIsLogonUserChanged, getUserMadeInteraction],
  (isLogonUserChanged, userMadeInteraction) => !isLogonUserChanged && userMadeInteraction
);

const getEvaluatedConditionsFactory = (state, { formKey }) => ({ id, index, entityName, prop }) =>
  getEvaluatedConditions(state, { id, index, entityName, prop, formKey });

const getChildrenIds = createSelector(
  [getFormModel, getComponentProps],
  (formModel, { id }) => formModel[id].childrenNodes ?? []
);

export const isChildrenHidden = createSelector(
  [getChildrenIds, getFormModel, getEvaluatedConditionsFactory, getComponentProps],
  (childrenIds, formModel, getEvaluatedConditionsFact, { index }) =>
    childrenIds.every((id) => {
      const childNode = formModel[id];
      const commonProps = { index, prop: FORM_MODEL_ATTRIBUTES.HIDDEN };

      if (childNode.controlType === SS_CONTROL_TYPE.ADVANCED_TEXT_BOX) {
        const textBoxId = childNode.childrenNodes.find(
          (advChildId) => formModel[advChildId].controlType === SS_CONTROL_TYPE.TEXT_BOX
        );

        return getEvaluatedConditionsFact({
          ...commonProps,
          id: textBoxId,
          entityName: formModel[textBoxId]?.entityName,
        });
      }

      return getEvaluatedConditionsFact({
        ...commonProps,
        id,
        entityName: childNode.entityName,
      });
    })
);

export const getChildrenTypes = createSelector(
  [getFormModel, getChildrenIds],
  (formModel, childrenIds) => childrenIds.map((children) => formModel[children].controlType)
);

export const getSavedSearchTitle = createSelector(
  [getSavedSearchResults, getComponentProps],
  (savedSearchResults, { id }) => {
    const savedSearchId = Object.keys(savedSearchResults).reduce((searchId, savedSearch) => {
      const intId = savedSearch.split('.').slice(-1)[0];
      if (intId === id) {
        searchId = savedSearch;
      }
      return searchId;
    }, '');
    const savedSearch = savedSearchResults[savedSearchId];
    return savedSearch?.results?.translatedSearchName || savedSearch?.results?.translatedEntityName;
  }
);

export const getDownloadTypeForSavedSearchRow = (rowId, deleteEnabled) =>
  createSelector(
    [getOfflineDataEntities, getEntitiesDownloadingStates],
    (entities, downloadingStates) => {
      const isEntityDownloading = downloadingStates.includes(rowId);

      if (isEntityDownloading) {
        return DOWNLOAD_ICON_ACTIONS_TYPES.DOWNLOADING;
      }

      const entityExists = _.some(entities, (item) => item.id === rowId && !_.isNil(item.entity));

      if (entityExists && !deleteEnabled) {
        return DOWNLOAD_ICON_ACTIONS_TYPES.DOWNLOADED;
      }

      if (entityExists && deleteEnabled) {
        return DOWNLOAD_ICON_ACTIONS_TYPES.DELETE;
      }

      return DOWNLOAD_ICON_ACTIONS_TYPES.DOWNLOAD;
    }
  );

export const getFieldLength = createSelector(
  [getEntityDefinition, getComponentProps],
  (entityDefinition, { entityName, fieldName }) =>
    getPropType(entityDefinition, `${entityName}.${fieldName}`, 'MaxLength')
);

export const getShowMenus = createSelector(
  [getWorkflowInfo],
  (workflowInfo) => !_.isNil(workflowInfo?.entityType)
);

export const getAttachmentGrid = createSelector([getFormModel], (formModel) =>
  Object.values(formModel).find((control) => control.gridType === 'Attachment')
);

export const getSignOffStepName = createSelector(
  [getFormData, getWorkflowInfo],
  (data, workflowInfo) => {
    const translatedStepName = data.formInfo?.translatedStepName;
    const stepName = data.formInfo?.stepName;

    return translatedStepName || workflowInfo.actionName || stepName || '';
  }
);

export const getIsExistingRecord = createSelector(
  [getEntityValues],
  (entityValues) => entityValues?.IsExistingObject ?? false
);

export const getTranslatedEmptyMessage = createSelector(
  [getTranslations, getComponentProps],
  (translations, { emptyMessage }) => translate(translations, emptyMessage)
);

export const getSharePointUrlParams = createSelector([getEntityData], (entityData) => {
  const {
    SCT_FILE_NAME: FileName,
    DOC_PHS_NAME: Phase,
    DOC_ID: DocId,
    SDC_SHAREPOINT_FILE_SEQ: SequenceNumber,
  } = _.get(entityData, ['V_SDC_SMART_DOCUMENT'], {});

  return {
    FileName,
    Phase,
    DocId,
    SequenceNumber,
  };
});

export const getEvaluatedHyperLinkExpression = createSelector(
  [
    (state, props) => getClosestParentWithEntityName(state, props),
    (state, props) => getSsContext(state, props),
    getComponentProps,
  ],
  (entityName, ssContext, { expression, controlIsInTable, isHyperlinkUrl }) => {
    if (expression?.includes('eval||')) {
      const expr = expression?.split('eval||')?.[1] ?? '';

      const evaluatedExpression = evalExpression(expr, {
        ...ssContext,
        controlIsInTable,
        contextDataMember: entityName,
        isHyperlinkUrl,
      });

      return evaluatedExpression ?? '';
    }
    return expression ?? '';
  }
);

export const getDecisionTreeQueryParams = createSelector(
  [
    (state, props) => getModelById(state, props),
    (state, props) => getSsContext(state, props),
    getToken,
  ],
  (model, ssContext, token) => {
    const initialQueryParams = {
      returnUrl: `${window.location.origin.toString()}/decision-tree-success.html`,
      Token: token,
    };

    return DECISION_TREE_QUERY_PARAM_NAMES.reduce((acc, prop) => {
      const propConfig = model?.[prop] ?? '';

      if (propConfig.includes('eval||')) {
        const expression = propConfig.split('eval||')[1];
        acc[prop] = evalSSEexpression(expression, ssContext);
        return acc;
      }

      if (propConfig) {
        acc[prop] = propConfig;
        return acc;
      }

      acc[prop] = '';

      return acc;
    }, initialQueryParams);
  }
);

export const getInspectionMeasurementDataQueryParams = createSelector([getToken], (token) => {
  const initialQueryParams = {
    Token: token,
  };
  return initialQueryParams;
});

export const getCourseNames = createSelector(
  [getEntityData, getEntityType],
  (entityData, entityType) => {
    const trainingInfoPath = [entityType, 'V_CUR_COURSE-V_DTI_DUT_TRAINING_INFO', 0];

    return [
      _.get(entityData, [...trainingInfoPath, 'DTI_SCORM_COURSE_TITLE']) ?? '',
      _.get(entityData, [...trainingInfoPath, 'DTI_SCORM_REF_CUR_TITLE']) ?? '',
      _.get(entityData, [...trainingInfoPath, 'DTI_SCORM_REV_CUR_TITLE']) ?? '',
    ];
  }
);

export const getCourseVersions = createSelector(
  [getEntityData, getEntityType],
  (entityData, entityType) => {
    const trainingInfoPath = [entityType, 'V_CUR_COURSE-V_DTI_DUT_TRAINING_INFO', 0];

    return [
      _.get(entityData, [...trainingInfoPath, 'DTI_SCORM_COURSE_VERSION'], null),
      _.get(entityData, [...trainingInfoPath, 'DTI_SCORM_REF_CUR_VERSION'], null),
      _.get(entityData, [...trainingInfoPath, 'DTI_SCORM_REV_CUR_VERSION'], null),
    ];
  }
);

export const hasVisibleZoomControl = createSelector(
  [getFormModel, (state, props) => getSsContext(state, props), getComponentProps],
  (formModel, ssContext, { id }) => {
    const ssZoom = getVisibleZoomByTextBoxId(formModel, id, ssContext);
    return !!ssZoom;
  }
);

export const getActiveTabs = createSelector(
  [getActiveContainers],
  (activeContainers) => activeContainers.activeTabs ?? []
);

export const getActiveSubGrids = createSelector(
  [getActiveContainers],
  (activeContainers) => activeContainers.activeSubGrids ?? []
);

export const getDynamicParamCollectionSelector = createSelector(
  [(state, props) => getSsContext(state, props), getComponentProps],
  (ssContext, { id }) => {
    const { paramList, dataLookUpType, dataObject } = ssContext.formModel[id];

    if (dataLookUpType === DATALOOKUP_TYPE_EXTERNAL_API) {
      return {
        RequestType: 'ExternalApi',
        ConfigName: dataObject,
        variables: Array.isArray(paramList?.paramListItems)
          ? getDynamicParamCollection(paramList?.paramListItems, ssContext)
          : {},
      };
    }

    if (!paramList) {
      return;
    }

    const paramListFormatted = Array.isArray(paramList.paramListItems)
      ? paramList.paramListItems
      : parseListString(paramList, 'Param');

    return Array.isArray(paramListFormatted)
      ? getDynamicParamCollection(paramListFormatted, ssContext)
      : undefined;
  }
);

export const isPanelWithButtons = createSelector(
  [getFormModel, getComponentProps],
  (formModel, { id }) => {
    const { childrenNodes } = formModel[id];

    return childrenNodes.every((child) => BUTTON_LIST.includes(formModel[child].controlType));
  }
);

export const isButtonDisabled = createSelector(
  [getWarnings, getComponentProps],
  (warnings, { buttonType, controlType }) => {
    const canBeDisabledBasedOnWarnings = warnings.some(
      (w) => w.source !== WARNING_SOURCES.VALIDATION
    );

    switch (controlType) {
      case SS_CONTROL_TYPE.BUTTON: {
        const canBeDisabledBasedOnButtonType = BUTTON_TYPES_SUPPORT_DISABLE.includes(buttonType);
        return canBeDisabledBasedOnButtonType && canBeDisabledBasedOnWarnings;
      }

      case SS_CONTROL_TYPE.BUTTON_BULK_UPLOAD:
      case SS_CONTROL_TYPE.BUTTON_BATCH:
      case SS_CONTROL_TYPE.BUTTON_CONTROL_PRINT: {
        return canBeDisabledBasedOnWarnings;
      }

      default:
        return false;
    }
  }
);

export const getFilteredChildrenNodesForTabSet = createSelector(
  [
    getIsFormChangesMode,
    getFormModel,
    (state, props) => getSsContext(state, props),
    getComponentProps,
  ],
  (isFormChangesMode, formModel, ssContext, { childrenNodes }) => {
    if (isFormChangesMode) {
      return childrenNodes;
    }

    return childrenNodes.filter((id) => {
      const { hidden } = formModel[id];

      if (['string', 'boolean'].includes(typeof hidden)) {
        return !isTrue(hidden);
      }

      if (typeof hidden === 'object') {
        return !evaluateConditions({
          conditions: hidden.Conditions,
          ssContext,
        });
      }

      return true;
    });
  }
);

export const getSelectedSubGridsCount = createSelector(
  [getSelectedSubGrids],
  (selectedSubGrids) => selectedSubGrids.length
);

const formHasSelectedSubGrids = createSelector(
  [getSelectedSubGridsCount],
  (subGridsCount) => subGridsCount > 0
);

export const stepFormHasSelectedSubGrids = (state) =>
  formHasSelectedSubGrids(state, { formKey: FORM_KEYS.STEP });

export const detailFormHasSelectedSubGrids = (state) =>
  formHasSelectedSubGrids(state, { formKey: FORM_KEYS.DETAIL });

export const anyFormHasSelectedSubGrids = createSelector(
  [stepFormHasSelectedSubGrids, detailFormHasSelectedSubGrids],
  (stepFormHasSubGrids, detailFormHasSubGrids) => stepFormHasSubGrids || detailFormHasSubGrids
);

export const getSelectedSubGridsWithActualIndexes = createSelector(
  [getSelectedSubGrids, getEntityData, getEntityDefinition, getPrimaryKeysMap],
  (selectedSubGrids, entityData, entityDefinition, primaryKeysMap) => {
    if (!selectedSubGrids.length) {
      return [];
    }

    const entityType = Object.keys(entityDefinition)[0];

    return selectedSubGrids.reduce((acc, subGrid) => {
      const { isParentlessTable, entityName, rowId } = subGrid;

      const entityNameIndexes = getIndexesForDataMember(entityData, entityType, entityName);
      const primaryKey = primaryKeysMap[entityName];

      const currentRowGridArrIndex = entityNameIndexes.find((index) => {
        const path = getDataPath(entityName, index, entityDefinition);
        const row = _.get(entityData, path);
        const rowKey = row[primaryKey] ?? row._newRowId;
        return rowKey === rowId;
      });

      const subGridWithActualIndexes = isParentlessTable
        ? {
            ...subGrid,
            row: { ...subGrid.row, gridArrIndex: currentRowGridArrIndex },
          }
        : {
            ...subGrid,
            gridIndex: currentRowGridArrIndex,
            row: { ...subGrid.row, index: _.last(currentRowGridArrIndex) },
          };

      return [...acc, subGridWithActualIndexes];
    }, []);
  }
);

export const getBladeGroupWidthArr = createSelector(
  [getSelectedSubGridsCount, pageHasErrors, getPanelLayout, getComponentProps],
  (subGridsCount, formHasErrors, { config }, { pathname }) => {
    switch (pathname) {
      case Urls.WORKFLOW_STEP:
        return getBladeGroupWidthArrWorkflowStepPage(formHasErrors, subGridsCount);

      case Urls.ENTITY_DISPLAY:
        return getBladeGroupWidthArrEntityDisplayPage(subGridsCount, config);

      case Urls.CERTIFICATION_PAGE:
        // TODO
        return SELECTED_SUB_GRID_DEFAULT_WIDTH;

      case Urls.DOCUMENT_VIEWER:
        // TODO
        return SELECTED_SUB_GRID_DEFAULT_WIDTH;

      default:
        return SELECTED_SUB_GRID_DEFAULT_WIDTH;
    }
  }
);

export const getFormFooterWidthPercentBasedOnSelectedSubGrids = createSelector(
  [getSelectedSubGridsCount, getComponentProps],
  (subGridsCount, { pathname, isMobileDevice }) => {
    if ([Urls.WORKFLOW_STEP, Urls.WORKFLOW_STEP].includes(pathname) && !isMobileDevice) {
      return getContainerWidthPercentWorkflowStepPage(subGridsCount);
    }

    return 100;
  }
);

export const getContainerWidthPercentBasedOnSelectedSubGrids = createSelector(
  [
    getSelectedSubGridsCount,
    stepFormHasSelectedSubGrids,
    detailFormHasSelectedSubGrids,
    getPanelLayout,
    getComponentProps,
  ],
  (
    subGridsCount,
    stepFormHasSubGrids,
    detailFormHasSubGrids,
    { config },
    { pathname, formKey, isMobileDevice, defaultWidthPercent = 100 }
  ) => {
    if (isMobileDevice) {
      return 100;
    }

    switch (pathname) {
      case Urls.WORKFLOW_STEP:
        return getContainerWidthPercentWorkflowStepPage(subGridsCount);

      case Urls.ENTITY_DISPLAY:
        return getContainerWidthPercentEntityDisplayPage({
          formKey,
          defaultWidthPercent,
          panelConfig: config,
          subGridsCount,
          stepFormHasSubGrids,
          detailFormHasSubGrids,
        });

      case Urls.CERTIFICATION_PAGE:
        // TODO
        return defaultWidthPercent;

      case Urls.DOCUMENT_VIEWER:
        // TODO
        return defaultWidthPercent;

      default:
        return defaultWidthPercent;
    }
  }
);

export const getSsModalMappingValues = createSelector(
  [getEntityData, getEntityDefinition, getComponentProps],
  (entityData, entityDefinition, { dataObject, dataMappingList, index, formKey }) => {
    if (formKey === FORM_KEYS.EDITOR) {
      return {};
    }

    const path = getDataPath(dataObject, index, entityDefinition);
    const row = _.get(entityData, path);

    return dataMappingList.reduce(
      (acc, { updateFrom, updateTo }) => ({ ...acc, [updateTo]: row[updateFrom] }),
      {}
    );
  }
);

export const getIsChildSsZoomStandalone = createSelector(
  [getFormModel, getComponentProps],
  (formModel, { id }) => {
    const childZoomId = formModel[id].childrenNodes?.find(
      (childId) => formModel[childId].controlType === SS_CONTROL_TYPE.ZOOM
    );

    return formModel[childZoomId]?.isStandalone;
  }
);

export const getIsSiblingSsZoomStandalone = createSelector(
  [getFormModel, getComponentProps],
  (formModel, { id }) => {
    const parentId = formModel[id]?.parent;
    const parent = formModel[parentId];

    if (parent?.controlType !== SS_CONTROL_TYPE.ADVANCED_TEXT_BOX) {
      return false;
    }

    const childZoomId = parent.childrenNodes?.find(
      (childId) => formModel[childId].controlType === SS_CONTROL_TYPE.ZOOM
    );

    return formModel[childZoomId]?.isStandalone;
  }
);

export const getMultiSelectDropDownValues = createSelector(
  [getEntityData, getEntityDefinition, getComponentProps],
  (entityData, entityDefinition, { entityName, fieldName, index, supportsMultipleOptions }) => {
    if (!supportsMultipleOptions) {
      return [];
    }

    const path = getDataPath(entityName, index, entityDefinition);
    const rows = _.get(entityData, path.slice(0, -1), []);

    const mappedRows = rows.map((row) => row[fieldName]).filter((value) => !_.isNil(value));

    return mappedRows;
  }
);

export const getSavedSearchResultsRows = createSelector(
  [getSavedSearchResults, getComponentProps],
  (savedSearchResults, { key }) => {
    return savedSearchResults[key]?.results?.rows;
  }
);

export const getWorkflowInfoProp = createSelector(
  [getWorkflowInfo, getComponentProps],
  (workflowInfo, { prop }) => workflowInfo[prop]
);

export const getEvaluatedClusterColumnChartData = createSelector(
  [(state, props) => getSsContext(state, props), getComponentProps],
  (ssContext, { id, formKey }) => {
    let { chartData } = ssContext.formModel[id];

    if (!isValidClusterColumnChartData(chartData)) {
      return null;
    }

    if (formKey === FORM_KEYS.EDITOR) {
      chartData = JSON.parse(updateRulesScripts(JSON.stringify(chartData), true));
      chartData = JSON.parse(updateRulesScripts(JSON.stringify(chartData)));
    }

    return chartData.map((chartItem) => {
      const label = evalSSEexpression(chartItem.label, ssContext);

      const clusterData = chartItem.clusterData.reduce((acc, clusterItem) => {
        const key = evalSSEexpression(clusterItem.label, ssContext);
        const value = evalSSEexpression(clusterItem.value, ssContext);

        acc[key] = value;
        return acc;
      }, {});

      return { label, data: [clusterData] };
    });
  }
);
